/**
 * EditorModals — All modal components grouped.
 * Extracted from FinancialModelEditor.tsx lines ~2341–2455 (Phase 3A).
 */

import React from 'react';
import { Snackbar, Alert } from '@mui/material';
import NewScenarioModal from '../../../components/NewScenarioModal';
import NewSensitivityModal from '../../../components/NewSensitivityModal';
import HistoryModal from '../../../components/HistoryModal';
import AuditabilityModal from '../../../components/AuditabilityModal';
import ReportsModal from '../../../components/ReportsModal';
import CompareModal from '../../../components/CompareModal';
import ErrorsModal from '../../../components/ErrorsModal';
import ErpSyncOverviewModal from '../../../components/ErpConnections/ErpSyncOverviewModal';
import ErrorPopover from '../../../components/ErrorPopover';
import { TailwindModal, TailwindButton } from '../../../components/Tailwind';
import apiService from '../../../services/ApiService';

interface EditorModalsProps {
    model: any;
    currentScenario: any;
    currentSensitivity: any;
    modelContext: any;
    // Modal open states
    newScenarioModalOpen: boolean;
    setNewScenarioModalOpen: (v: boolean) => void;
    newSensitivityModalOpen: boolean;
    setNewSensitivityModalOpen: (v: boolean) => void;
    historyModalOpen: boolean;
    setHistoryModalOpen: (v: boolean) => void;
    auditModalOpen: boolean;
    setAuditModalOpen: (v: boolean) => void;
    reportsModalOpen: boolean;
    setReportsModalOpen: (v: boolean) => void;
    compareModalOpen: boolean;
    setCompareModalOpen: (v: boolean) => void;
    errorsModalOpen: boolean;
    setErrorsModalOpen: (v: boolean) => void;
    sensitivityErrorsModalOpen: boolean;
    setSensitivityErrorsModalOpen: (v: boolean) => void;
    erpSyncModalOpen: boolean;
    setErpSyncModalOpen: (v: boolean) => void;
    scenarioDeleteOpen: boolean;
    setScenarioDeleteOpen: (v: boolean) => void;
    sensitivityDeleteOpen: boolean;
    setSensitivityDeleteOpen: (v: boolean) => void;
    errorsAnchorEl: any;
    setErrorsAnchorEl: (v: any) => void;
    // Snackbar
    contextSnackBarContent: { open: boolean; severity: string; message: string };
    setContextSnackBarContent: (v: { open: boolean; severity: string; message: string }) => void;
    // Data
    firstError: string | null;
    getAllComputeErrors: () => any[];
    getSensitivityErrors: () => any[];
    navigateToError: (error: any) => void;
    navigateToSensitivityError: (error: any) => void;
    handleErpSyncComplete: (fieldId: number, newValue: any, fieldKey?: string | null) => void;
    getTimingContext: (fieldId: number) => any;
    // Scenario/Sensitivity actions
    setCurrentScenario: React.Dispatch<any>;
    setCurrentSensitivity: React.Dispatch<any>;
}

export default function EditorModals({
    model,
    currentScenario,
    currentSensitivity,
    modelContext,
    newScenarioModalOpen,
    setNewScenarioModalOpen,
    newSensitivityModalOpen,
    setNewSensitivityModalOpen,
    historyModalOpen,
    setHistoryModalOpen,
    auditModalOpen,
    setAuditModalOpen,
    reportsModalOpen,
    setReportsModalOpen,
    compareModalOpen,
    setCompareModalOpen,
    errorsModalOpen,
    setErrorsModalOpen,
    sensitivityErrorsModalOpen,
    setSensitivityErrorsModalOpen,
    erpSyncModalOpen,
    setErpSyncModalOpen,
    scenarioDeleteOpen,
    setScenarioDeleteOpen,
    sensitivityDeleteOpen,
    setSensitivityDeleteOpen,
    errorsAnchorEl,
    setErrorsAnchorEl,
    contextSnackBarContent,
    setContextSnackBarContent,
    firstError,
    getAllComputeErrors,
    getSensitivityErrors,
    navigateToError,
    navigateToSensitivityError,
    handleErpSyncComplete,
    getTimingContext,
    setCurrentScenario,
    setCurrentSensitivity,
}: EditorModalsProps) {
    return (
        <>
            <NewScenarioModal
                modelScenarios={model.modelScenarios}
                modelId={model.id}
                newScenarioModalOpen={newScenarioModalOpen}
                setNewScenarioModalOpen={setNewScenarioModalOpen}
                setCurrentScenario={(scenario: any) => {
                    setCurrentScenario(scenario);
                    if (modelContext) modelContext.refreshModel();
                }}
                setSocketUrl={() => {}}
            />
            <NewSensitivityModal
                sensitivities={currentScenario.sensitivities}
                scenarioId={currentScenario.id}
                newSensitivityModalOpen={newSensitivityModalOpen}
                setNewSensitivityModalOpen={setNewSensitivityModalOpen}
                setCurrentSensitivity={(sensitivity: any) => {
                    setCurrentSensitivity(sensitivity);
                    if (modelContext) modelContext.refreshModel();
                }}
            />
            <HistoryModal open={historyModalOpen} setOpen={setHistoryModalOpen} model={model} currentScenario={currentScenario} />
            <AuditabilityModal open={auditModalOpen} setOpen={setAuditModalOpen} scenario={currentScenario} sensitivity={currentSensitivity} />
            <ReportsModal open={reportsModalOpen} setOpen={setReportsModalOpen} model={model} scenario={currentScenario} sensitivity={currentSensitivity} />
            <CompareModal open={compareModalOpen} setOpen={setCompareModalOpen} model={model} scenarios={model.modelScenarios} />
            <ErrorsModal isOpen={errorsModalOpen} onClose={() => setErrorsModalOpen(false)} errors={getAllComputeErrors()} onErrorClick={navigateToError} />
            <ErrorsModal isOpen={sensitivityErrorsModalOpen} onClose={() => setSensitivityErrorsModalOpen(false)} errors={getSensitivityErrors()} onErrorClick={navigateToSensitivityError} title={`Sensitivity Errors (${getSensitivityErrors().length})`} />
            <ErpSyncOverviewModal open={erpSyncModalOpen} setOpen={setErpSyncModalOpen} scenarioId={currentScenario.id} scenarioName={currentScenario.name} onSyncComplete={handleErpSyncComplete} getTimingContext={getTimingContext} />
            <ErrorPopover anchorEl={errorsAnchorEl} setAnchorEl={setErrorsAnchorEl} firstError={firstError} />

            {/* Delete Scenario Modal */}
            <TailwindModal
                isOpen={scenarioDeleteOpen}
                onClose={() => setScenarioDeleteOpen(false)}
                title="Are you sure?"
                size="md"
                footer={
                    <div className="flex gap-3 justify-center w-full">
                        <TailwindButton
                            variant="secondary"
                            onClick={() => setScenarioDeleteOpen(false)}
                            icon="close"
                        >
                            Cancel
                        </TailwindButton>
                        <TailwindButton
                            variant="danger"
                            onClick={() => {
                                apiService.deleteModelScenario(currentScenario.id).then(async (response) => {
                                    if (response) {
                                        const index = model.modelScenarios.indexOf(currentScenario);
                                        model.modelScenarios.splice(index, 1);
                                        const nextScenario = model.modelScenarios[index > 0 ? index - 1 : 0];
                                        setCurrentScenario(nextScenario);
                                        setCurrentSensitivity('null');
                                        setScenarioDeleteOpen(false);
                                        if (modelContext) modelContext.refreshModel();
                                    }
                                });
                            }}
                            icon="delete_forever"
                        >
                            Yes, Delete
                        </TailwindButton>
                    </div>
                }
            >
                <p className="text-center text-slate-700">
                    This will delete <strong>{currentScenario.name}</strong> and all its content permanently.
                </p>
            </TailwindModal>

            {/* Delete Sensitivity Modal */}
            <TailwindModal
                isOpen={sensitivityDeleteOpen}
                onClose={() => setSensitivityDeleteOpen(false)}
                title="Are you sure?"
                size="md"
                footer={
                    <div className="flex gap-3 justify-center w-full">
                        <TailwindButton
                            variant="secondary"
                            onClick={() => setSensitivityDeleteOpen(false)}
                            icon="close"
                        >
                            Cancel
                        </TailwindButton>
                        <TailwindButton
                            variant="danger"
                            onClick={() => {
                                if (currentSensitivity === 'null') return;
                                apiService.deleteSensitivity(currentSensitivity.id).then(async (response) => {
                                    if (response) {
                                        const index = currentScenario.sensitivities.indexOf(currentSensitivity);
                                        currentScenario.sensitivities.splice(index, 1);
                                        setCurrentSensitivity('null');
                                        setSensitivityDeleteOpen(false);
                                        if (modelContext) modelContext.refreshModel();
                                    }
                                });
                            }}
                            icon="delete_forever"
                        >
                            Yes, Delete
                        </TailwindButton>
                    </div>
                }
            >
                <p className="text-center text-slate-700">
                    This will delete <strong>{currentSensitivity !== 'null' ? currentSensitivity.name : ''}</strong> and all its content permanently.
                </p>
            </TailwindModal>

            <Snackbar open={contextSnackBarContent.open} autoHideDuration={6000} onClose={() => { setContextSnackBarContent({ open: false, severity: 'success', message: '' }); }}>
                <Alert
                    onClose={() => { setContextSnackBarContent({ open: false, severity: 'success', message: '' }); }}
                    // @ts-ignore
                    severity={contextSnackBarContent.severity}
                    sx={{ width: '100%' }}
                >
                    {contextSnackBarContent.message}
                </Alert>
            </Snackbar>
        </>
    );
}
